import type { TFunction } from 'i18next';
import {
  buildMediaUnitFieldColumns,
  type MediaUnitFieldColumnLabels,
} from '../../../lib/media-unit-field-columns';
import {
  OFFER_MEDIA_UNIT_SORT_KEY_ALIASES,
  type OfferMediaUnitFieldId,
} from '../../../lib/offer-media-unit-fields';
import type { OfferUnitItem } from './units-tab-columns';

function getValue(row: OfferUnitItem, fieldId: OfferMediaUnitFieldId): unknown {
  const values: Record<OfferMediaUnitFieldId, unknown> = {
    mediaType: row.mediaType,
    formatCode: row.formatCode,
    supplierId: row.supplier,
    supplierNumber: row.supplierNumber,
    grwId: row.grwId,
    region: row.region,
    county: row.county,
    agglomeration: row.agglomeration,
    city: row.city,
    address: row.address,
    width: row.widthM,
    height: row.heightM,
    audience: row.audienceEstimate,
    notes: row.notes,
    wfContactPerson: row.wfContactPerson,
    locationDescription: row.locationDescription,
    noPhoto: row.hasPhoto,
    lighting: row.illuminated,
    material: row.material,
    multiformat: row.multiformat,
    tags: row.tags,
    roadNumber: row.roadNumber,
    area: row.areaSqm,
  };
  return values[fieldId];
}

function buildLabels(t: TFunction<'offers'>): MediaUnitFieldColumnLabels {
  const fields = [
    ['mediaType', 'mediaType'],
    ['format', 'format'],
    ['supplier', 'supplier'],
    ['supplierNumber', 'supplierNumber'],
    ['grwId', 'grwId'],
    ['region', 'region'],
    ['county', 'county'],
    ['agglomeration', 'agglomeration'],
    ['city', 'city'],
    ['address', 'address'],
    ['dimensions', 'dimensions'],
    ['audienceEstimate', 'audienceEstimate'],
    ['notes', 'notes'],
    ['wfContactPerson', 'wfContactPerson'],
    ['locationDescription', 'locationDescription'],
    ['noPhoto', 'noPhoto'],
    ['illuminated', 'lighting'],
    ['material', 'material'],
    ['multiformat', 'multiformat'],
    ['tags', 'tags'],
    ['roadNumber', 'roadNumber'],
    ['area', 'area'],
  ] as const;
  return {
    columns: Object.fromEntries(
      fields.map(([id, key]) => [id, t(`table.columns.${key}`, { ns: 'inventory' })]),
    ),
    trueLabel: t('bool.yes', { ns: 'common' }),
    falseLabel: t('bool.no', { ns: 'common' }),
    noPhotoAria: t('table.noPhotoAria', { ns: 'inventory' }),
  };
}

/**
 * `_canViewSupplierDetails` is accepted for call-site compatibility (callers
 * still thread the same permission value through for filter-field gating)
 * but is no longer used here — the supplier.view-gated columns stay present
 * for every caller and mask per row instead (field-visibility).
 */
export function buildUnitsSharedColumns(t: TFunction<'offers'>, _canViewSupplierDetails: boolean) {
  return buildMediaUnitFieldColumns<OfferUnitItem>({
    surface: 'unified',
    adapter: {
      getValue,
      getDimensionNotes: (row) => row.dimensionNotes,
      getFormatLabel: (row) => row.format,
      getSupplierWithheld: (row) => row.supplierWithheld,
    },
    labels: buildLabels(t),
    supplierMaskedExplanation: t('workspace.unitsTab.supplierMaskedExplanation'),
    sortKeys: OFFER_MEDIA_UNIT_SORT_KEY_ALIASES,
  });
}
